
/*
  =====================================================
  NEMEAN NAVIGATOR — STEUERUNG V2.1
  Datei: assets/nemean-navigator.js

  - Bestehende Ziel- und Werkzeugnavigation
  - Gemeinsame Nutzereingaben
  - Drei Rechner
  - Erweiterte Energieanalyse
  - Zielbezogene Erklärungen
  - Evidenzorientierter Supplement-Kompass
  - Keine Datenspeicherung
  - Keine externen Datenanfragen

  Berechnungskern bleibt:
  assets/nemean-navigator-calc.js
  =====================================================
*/

(function () {
  "use strict";

  var ROOT_SELECTOR = "[data-nnav-section]";

  var VALID_GOALS = [
    "muscle",
    "weight",
    "balance",
    "recover"
  ];

  var VALID_TOOLS = [
    "calories",
    "protein",
    "macros"
  ];

  var GOAL_CONTENT = {
    muscle: {
      title: "MUSKELN AUFBAUEN",
      description:
        "Wir berechnen einen beispielhaften " +
        "Energieüberschuss von 5 Prozent. " +
        "Für den Protein- oder Makrorechner " +
        "kannst du einen sportbezogenen " +
        "Proteinmodus auswählen."
    },

    weight: {
      title: "GEWICHT MANAGEN",
      description:
        "Du entscheidest, ob du Gewicht " +
        "reduzieren, halten oder erhöhen " +
        "möchtest. Deine Auswahl bestimmt " +
        "das rechnerische Kalorienziel."
    },

    balance: {
      title: "BALANCE FINDEN",
      description:
        "Der geschätzte Erhaltungsbedarf " +
        "bildet den Ausgangspunkt. " +
        "Eine automatische Kalorienerhöhung " +
        "oder -reduktion erfolgt nicht."
    },

    recover: {
      title: "BESSER REGENERIEREN",
      description:
        "Der geschätzte Erhaltungsbedarf " +
        "dient als Orientierung. " +
        "Der Rechner kann Energie- und " +
        "Proteinwerte einordnen, aber " +
        "weder Schlafqualität noch " +
        "Regeneration individuell messen."
    }
  };

  var numberFormatter = new Intl.NumberFormat(
    "de-DE",
    {
      maximumFractionDigits: 0
    }
  );

  var decimalFormatter = new Intl.NumberFormat(
    "de-DE",
    {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    }
  );

  function formatNumber(value) {
    return numberFormatter.format(Math.round(value));
  }

  function formatDecimal(value) {
    return decimalFormatter.format(value);
  }

  function formatSigned(value) {
    var rounded = Math.round(value);

    if (rounded > 0) {
      return "+" + formatNumber(rounded);
    }

    return formatNumber(rounded);
  }

  function getElement(root, selector) {
    return root.querySelector(selector);
  }

  function getFormField(form, name) {
    return form.elements.namedItem(name);
  }

  function getFieldValue(form, name) {
    var field = getFormField(form, name);
    return field ? field.value : "";
  }

  function setText(root, key, value) {
    var element = getElement(
      root,
      '[data-nnav-result="' + key + '"]'
    );

    if (element) {
      element.textContent = value;
    }
  }

  function readInputs(form, goal) {
    return {
      age: getFieldValue(form, "age"),
      height: getFieldValue(form, "height"),
      weight: getFieldValue(form, "weight"),
      formula: getFieldValue(form, "formula"),
      activity: getFieldValue(form, "activity"),
      goal: goal,
      weightGoal: getFieldValue(
        form,
        "weightGoal"
      ),
      protein: getFieldValue(form, "protein")
    };
  }

  function hideError(element) {
    if (!element) return;

    element.textContent = "";
    element.hidden = true;
  }

  function showError(element, message) {
    if (!element) return;

    element.textContent = message;
    element.hidden = false;
  }

  function combinedNote(text, warnings) {
    var parts = [];

    if (text) {
      parts.push(text);
    }

    if (
      Array.isArray(warnings) &&
      warnings.length
    ) {
      parts.push(
        "Wichtiger Hinweis: " +
        warnings.join(" ")
      );
    }

    return parts.join(" ");
  }

  function showRelevantPanel(root, tool) {
    root.querySelectorAll(
      "[data-nnav-result-panel]"
    ).forEach(function (panel) {
      panel.hidden =
        panel.getAttribute(
          "data-nnav-result-panel"
        ) !== tool;
    });
  }

  /*
    ENERGIEANALYSE
  */

  function fillEnergyOverview(root, data) {
    var calories = data.calories;

    var resting = calories.resting;
    var maintenance = calories.maintenance;
    var target = calories.target;

    var activityDifference =
      maintenance - resting;

    var targetDifference =
      target - maintenance;

    var percent =
      maintenance > 0
        ? targetDifference / maintenance * 100
        : 0;

    setText(
      root,
      "energy-resting",
      formatNumber(resting)
    );

    setText(
      root,
      "energy-maintenance",
      formatNumber(maintenance)
    );

    setText(
      root,
      "energy-target",
      formatNumber(target)
    );

    setText(
      root,
      "energy-difference",
      formatSigned(targetDifference)
    );

    setText(
      root,
      "energy-difference-label",
      "kcal / Tag (" +
      (percent > 0 ? "+" : "") +
      formatDecimal(percent) +
      " %)"
    );

    setText(
      root,
      "energy-activity-difference",
      "+" +
      formatNumber(activityDifference) +
      " kcal / Tag"
    );

    setText(
      root,
      "energy-activity-explanation",
      "Die Differenz zwischen deinem " +
      "geschätzten Grundumsatz von " +
      formatNumber(resting) +
      " kcal und deinem Gesamtumsatz von " +
      formatNumber(maintenance) +
      " kcal entsteht rechnerisch durch " +
      "den gewählten Aktivitätsfaktor. " +
      "Sie ist keine separat gemessene " +
      "Menge an Trainingskalorien."
    );
  }

  /*
    ZIELSTRATEGIE
  */

  function getStrategy(data) {
    var inputs = data.inputs;
    var calories = data.calories;

    var delta =
      calories.target -
      calories.maintenance;

    var percent =
      (delta / calories.maintenance) * 100;

    var strategy = {
      title: "ENERGIEBEDARF EINORDNEN",

      text:
        "Das Kalorienziel dient als " +
        "rechnerischer Ausgangspunkt.",

      caution:
        "Ein Energiebedarfsrechner " +
        "liefert keine individuell " +
        "gemessenen Werte.",

      progress:
        "Beobachte deine Entwicklung " +
        "über mehrere Wochen und " +
        "beurteile sie nicht anhand " +
        "einzelner Tage."
    };

    if (inputs.goal === "muscle") {
      strategy.title =
        "MODERATER ÜBERSCHUSS";

      strategy.text =
        "Für den Muskelaufbau verwendet " +
        "der Navigator einen beispielhaften " +
        "Energieüberschuss von 5 Prozent. " +
        "Ausreichende Energie kann " +
        "Krafttraining und Muskelaufbau " +
        "unterstützen. Entscheidend sind " +
        "vor allem ein geeigneter " +
        "Trainingsreiz und eine " +
        "bedarfsgerechte Proteinzufuhr.";

      strategy.caution =
        "Nicht jeder Muskelaufbau benötigt " +
        "einen Überschuss. Besonders " +
        "bei Trainingsanfängern oder " +
        "höheren Fettreserven kann auch " +
        "Muskelaufbau bei ungefähr " +
        "erhaltender Energiezufuhr " +
        "möglich sein.";

      strategy.progress =
        "Beobachte Trainingsleistung, " +
        "Körpergewicht und gegebenenfalls " +
        "Körperumfänge über mehrere Wochen. " +
        "Ein steigendes Gewicht allein " +
        "beweist keinen Muskelzuwachs.";
    }

    if (inputs.goal === "weight") {
      if (inputs.weightGoal === "lose") {
        strategy.title =
          "MODERATES KALORIENDEFIZIT";

        strategy.text =
          "Für die Gewichtsreduktion " +
          "berechnet der Navigator " +
          "beispielhaft 10 Prozent " +
          "weniger Energie als den " +
          "geschätzten Erhaltungsbedarf. " +
          "Ein Energiedefizit ist " +
          "eine Grundlage für den " +
          "Abbau von Körpermasse.";

        strategy.caution =
          "Ein größeres Defizit ist " +
          "nicht automatisch besser. " +
          "Zu geringe Energiezufuhr " +
          "kann die Nährstoffversorgung, " +
          "Leistungsfähigkeit und " +
          "den Muskelerhalt erschweren.";

        strategy.progress =
          "Beobachte den Gewichtstrend " +
          "über mehrere Wochen statt " +
          "einzelne Tageswerte. " +
          "Wasserhaushalt und Verdauung " +
          "können kurzfristige " +
          "Schwankungen verursachen.";
      } else if (
        inputs.weightGoal === "gain"
      ) {
        strategy.title =
          "MODERATER KALORIENÜBERSCHUSS";

        strategy.text =
          "Für eine Gewichtszunahme " +
          "berechnet der Navigator " +
          "beispielhaft 5 Prozent " +
          "mehr Energie als den " +
          "geschätzten Erhaltungsbedarf.";

        strategy.caution =
          "Die gewünschte " +
          "Gewichtsentwicklung hängt " +
          "auch von Ernährung, Bewegung " +
          "und individuellen Faktoren ab.";

        strategy.progress =
          "Beobachte den Gewichtstrend " +
          "und dein Wohlbefinden über " +
          "mehrere Wochen. Bei " +
          "ungewolltem Gewichtsverlust " +
          "ist eine medizinische " +
          "Abklärung sinnvoll.";
      } else {
        strategy.title =
          "GEWICHT STABIL HALTEN";

        strategy.text =
          "Das rechnerische Kalorienziel " +
          "entspricht deinem geschätzten " +
          "Erhaltungsbedarf. " +
          "Die tatsächliche Energiebilanz " +
          "lässt sich anhand des " +
          "längerfristigen Gewichtsverlaufs " +
          "besser einschätzen.";

        strategy.progress =
          "Beobachte den Gewichtstrend " +
          "über mehrere Wochen. " +
          "Kleine tägliche Schwankungen " +
          "sind normal.";
      }
    }

    if (inputs.goal === "balance") {
      strategy.title =
        "ENERGIEBALANCE";

      strategy.text =
        "Der Navigator orientiert " +
        "sich am geschätzten " +
        "Erhaltungsbedarf. " +
        "Im Mittelpunkt stehen eine " +
        "ausgewogene Ernährung, " +
        "angemessene Aktivität und " +
        "eine ausreichende Versorgung " +
        "mit wichtigen Nährstoffen.";

      strategy.caution =
        "Aus den Rechnerangaben " +
        "lassen sich keine " +
        "Vitamin- oder Mineralstoffmängel " +
        "feststellen.";

      strategy.progress =
        "Beobachte über mehrere Wochen " +
        "deine Energie im Alltag, " +
        "deine Essgewohnheiten und " +
        "deine Gewichtsentwicklung.";
    }

    if (inputs.goal === "recover") {
      strategy.title =
        "ENERGIE FÜR REGENERATION";

      strategy.text =
        "Bei körperlicher Belastung " +
        "kann ausreichende Energie- " +
        "und Proteinzufuhr wichtig " +
        "für die Erholung sein. " +
        "Der Navigator verwendet " +
        "den geschätzten " +
        "Erhaltungsbedarf als " +
        "Ausgangspunkt.";

      strategy.caution =
        "Der Rechner kann weder " +
        "Schlafqualität noch " +
        "individuellen Regenerationsbedarf " +
        "bestimmen. Bei anhaltender " +
        "Erschöpfung oder Schlafproblemen " +
        "sollten Ursachen fachlich " +
        "abgeklärt werden.";

      strategy.progress =
        "Beobachte Schlafgewohnheiten, " +
        "Belastung, Erholung und " +
        "Trainingsleistung im Verlauf. " +
        "Ein Kalorienwert allein " +
        "beschreibt nicht deine " +
        "Regeneration.";
    }

    strategy.delta = delta;
    strategy.percent = percent;

    return strategy;
  }

  function fillStrategy(root, data) {
    var strategy = getStrategy(data);

    var delta = strategy.delta;
    var percent = strategy.percent;

    var balanceLabel =
      delta > 0.5
        ? "ÜBERSCHUSS"
        : delta < -0.5
          ? "DEFIZIT"
          : "ERHALT";

    setText(
      root,
      "strategy-title",
      strategy.title
    );

    setText(
      root,
      "strategy-text",
      strategy.text
    );

    setText(
      root,
      "strategy-balance",
      balanceLabel
    );

    setText(
      root,
      "strategy-percent",
      (percent > 0 ? "+" : "") +
      formatDecimal(percent) +
      " %"
    );

    setText(
      root,
      "strategy-caution",
      strategy.caution
    );

    setText(
      root,
      "progress-advice",
      strategy.progress
    );
  }

  /*
    SUPPLEMENT-KOMPASS

    Keine Produktlinks oder Kaufbuttons.
    Keine Einnahmevorgaben.
    Evidenz nach Einsatzgebiet einordnen.
  */

  var SUPPLEMENT_LIBRARY = {
    creatine: {
      label: "GUT UNTERSUCHT IM KRAFTSPORT",
      name: "Kreatin Monohydrat",
      relevance:
        "Relevant für wiederholte kurze, " +
        "intensive körperliche Belastungen " +
        "und Krafttraining.",
      evidence:
        "Gut untersuchte Wirkung auf " +
        "die Leistungsfähigkeit bei " +
        "kurzzeitigen, intensiven " +
        "Belastungen unter den " +
        "entsprechenden Bedingungen.",
      caution:
        "Nicht automatisch für jede " +
        "Person erforderlich. Bei " +
        "Nierenerkrankungen oder " +
        "anderen relevanten Erkrankungen " +
        "ist fachliche Rücksprache sinnvoll."
    },

    protein: {
      label: "ERNÄHRUNGSABHÄNGIGE ERGÄNZUNG",
      name: "Whey / Clear Whey",
      relevance:
        "Kann die tägliche " +
        "Proteinversorgung praktisch " +
        "ergänzen, wenn die normale " +
        "Ernährung nicht ausreicht.",
      evidence:
        "Protein trägt zum Aufbau " +
        "und Erhalt von Muskelmasse bei. " +
        "Ein Pulver bietet gegenüber " +
        "ausreichend proteinreichen " +
        "Lebensmitteln keinen " +
        "grundsätzlichen Pflichtvorteil.",
      caution:
        "Bei Unverträglichkeiten " +
        "und bestimmten Erkrankungen " +
        "müssen Zutaten und " +
        "individueller Bedarf " +
        "berücksichtigt werden."
    },

    magnesium: {
      label: "VERSORGUNG PRÜFEN",
      name: "Magnesium",
      relevance:
        "Magnesium ist für " +
        "normale Muskelfunktion " +
        "und Energiestoffwechsel " +
        "relevant.",
      evidence:
        "Eine ergänzende Zufuhr " +
        "ist insbesondere dann " +
        "relevant, wenn die " +
        "Versorgung unzureichend ist.",
      caution:
        "Aus dem Navigator lässt " +
        "sich kein Magnesiummangel " +
        "ableiten. Kein pauschales " +
        "Versprechen für besseren " +
        "Schlaf oder weniger Stress."
    },

    omega3: {
      label: "ERNÄHRUNGSKONTEXT",
      name: "Omega-3",
      relevance:
        "Die Versorgung mit " +
        "Omega-3-Fettsäuren hängt " +
        "unter anderem von den " +
        "Ernährungsgewohnheiten ab.",
      evidence:
        "Die Evidenz ist " +
        "anwendungsabhängig. " +
        "Ein allgemeiner Vorteil " +
        "für Regeneration oder " +
        "Wohlbefinden kann nicht " +
        "pauschal versprochen werden.",
      caution:
        "Besonders bei " +
        "gerinnungshemmenden " +
        "Arzneimitteln ist eine " +
        "individuelle fachliche " +
        "Prüfung wichtig."
    }
  };

  function createTextElement(
    tag,
    className,
    text
  ) {
    var element =
      document.createElement(tag);

    if (className) {
      element.className = className;
    }

    element.textContent = text;

    return element;
  }

  function addSupplementCard(
    container,
    supplement
  ) {
    var card = document.createElement("article");

    card.className =
      "nnav21__supplement";

    card.appendChild(
      createTextElement(
        "span",
        "nnav21__supplement-label",
        supplement.label
      )
    );

    card.appendChild(
      createTextElement(
        "h4",
        "",
        supplement.name
      )
    );

    var relevance =
      document.createElement("p");

    var relevanceTitle =
      document.createElement("strong");

    relevanceTitle.textContent =
      "Relevanz: ";

    relevance.appendChild(relevanceTitle);

    relevance.appendChild(
      document.createTextNode(
        supplement.relevance
      )
    );

    card.appendChild(relevance);

    var evidence =
      document.createElement("p");

    var evidenceTitle =
      document.createElement("strong");

    evidenceTitle.textContent =
      "Einordnung: ";

    evidence.appendChild(evidenceTitle);

    evidence.appendChild(
      document.createTextNode(
        supplement.evidence
      )
    );

    card.appendChild(evidence);

    var caution =
      document.createElement("p");

    var cautionTitle =
      document.createElement("strong");

    cautionTitle.textContent =
      "Grenzen: ";

    caution.appendChild(cautionTitle);

    caution.appendChild(
      document.createTextNode(
        supplement.caution
      )
    );

    card.appendChild(caution);

    container.appendChild(card);
  }

  function fillSupplements(root, data) {
    var container = getElement(
      root,
      "[data-nnav-supplements]"
    );

    if (!container) return;

    container.replaceChildren();

    var goal = data.inputs.goal;
    var selected = [];

    if (goal === "muscle") {
      selected = [
        "creatine",
        "protein"
      ];
    } else if (goal === "weight") {
      selected = [
        "protein"
      ];
    } else if (goal === "balance") {
      selected = [
        "magnesium",
        "omega3"
      ];
    } else if (goal === "recover") {
      selected = [
        "protein",
        "magnesium"
      ];
    }

    selected.forEach(function (key) {
      var supplement =
        SUPPLEMENT_LIBRARY[key];

      if (supplement) {
        addSupplementCard(
          container,
          supplement
        );
      }
    });

    if (selected.length === 0) {
      container.appendChild(
        createTextElement(
          "p",
          "",
          "Für diese Auswahl liegen " +
          "keine pauschalen " +
          "Supplement-Empfehlungen vor."
        )
      );
    }
  }

  /*
    KALORIENRECHNER
  */

  function fillCalorieResults(root, data) {
    var calories = data.calories;

    setText(
      root,
      "calories-maintenance",
      formatNumber(calories.maintenance)
    );

    setText(
      root,
      "calories-target",
      formatNumber(calories.target)
    );

    setText(
      root,
      "calories-goal-note",
      combinedNote(
        calories.goalNote,
        calories.warnings
      )
    );

    setText(
      root,
      "calories-explanation",
      calories.explanation
    );
  }

  /*
    PROTEINRECHNER
  */

  function fillProteinResults(root, data) {
    var protein = data.protein;

    setText(
      root,
      "protein-grams",
      formatNumber(protein.grams) + " g"
    );

    setText(
      root,
      "protein-factor",
      formatDecimal(protein.factor)
    );

    setText(
      root,
      "protein-note",
      combinedNote(
        protein.note,
        protein.warnings
      )
    );

    setText(
      root,
      "protein-explanation",
      protein.explanation
    );
  }

  /*
    MAKRORECHNER
  */

  function fillMacroResults(root, data) {
    var macros = data.macros;

    setText(
      root,
      "macros-calories",
      formatNumber(macros.calories)
    );

    if (!macros.valid) {
      [
        "macros-protein",
        "macros-fat",
        "macros-carbs",
        "macros-protein-percent",
        "macros-fat-percent",
        "macros-carbs-percent"
      ].forEach(function (key) {
        setText(root, key, "–");
      });

      setText(
        root,
        "macros-note",
        macros.note
      );

      setText(
        root,
        "macros-explanation",
        macros.explanation
      );

      return;
    }

    setText(
      root,
      "macros-protein",
      formatNumber(
        macros.proteinGrams
      ) + " g"
    );

    setText(
      root,
      "macros-fat",
      formatNumber(
        macros.fatGrams
      ) + " g"
    );

    setText(
      root,
      "macros-carbs",
      formatNumber(
        macros.carbGrams
      ) + " g"
    );

    setText(
      root,
      "macros-protein-percent",
      formatDecimal(
        macros.proteinPercent
      ) + " % der Energie"
    );

    setText(
      root,
      "macros-fat-percent",
      formatDecimal(
        macros.fatPercent
      ) + " % der Energie"
    );

    setText(
      root,
      "macros-carbs-percent",
      formatDecimal(
        macros.carbPercent
      ) + " % der Energie"
    );

    setText(
      root,
      "macros-note",
      combinedNote(
        macros.note,
        macros.warnings
      )
    );

    setText(
      root,
      "macros-explanation",
      macros.explanation
    );
  }

  function fillAllResults(root, data) {
    fillEnergyOverview(root, data);
    fillStrategy(root, data);
    fillCalorieResults(root, data);
    fillProteinResults(root, data);
    fillMacroResults(root, data);
    fillSupplements(root, data);
  }

  /*
    SCROLL
  */

  function scrollToElement(element) {
    if (!element) return;

    if (
      typeof element.scrollIntoView !==
      "function"
    ) {
      return;
    }

    var reducedMotion =
      window.matchMedia &&
      window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;

    element.scrollIntoView({
      behavior:
        reducedMotion ? "auto" : "smooth",
      block: "start"
    });
  }

  /*
    INITIALISIERUNG
  */

  function initNavigator(root) {
    if (!root) return;

    if (
      root.dataset.nnavInitialized === "1"
    ) {
      return;
    }

    var form = getElement(
      root,
      "[data-nnav-form]"
    );

    var results = getElement(
      root,
      "[data-nnav-results]"
    );

    var errorElement = getElement(
      root,
      "[data-nnav-error]"
    );

    var goalButtons = root.querySelectorAll(
      ".nnav__goal[data-goal]"
    );

    var toolButtons = root.querySelectorAll(
      "[data-nnav-tool]"
    );

    var weightArea = getElement(
      root,
      "[data-nnav-weight-area]"
    );

    var proteinArea = getElement(
      root,
      "[data-nnav-protein-area]"
    );

    var feedback = getElement(
      root,
      "[data-nnav-goal-feedback]"
    );

    var feedbackTitle = getElement(
      root,
      "[data-nnav-goal-title]"
    );

    var feedbackDescription = getElement(
      root,
      "[data-nnav-goal-description]"
    );

    var feedbackWeightArea = getElement(
      root,
      "[data-nnav-quick-weight-area]"
    );

    var quickWeightSelect = getElement(
      root,
      "[data-nnav-quick-weight]"
    );

    var nextButton = getElement(
      root,
      "[data-nnav-goal-next]"
    );

    var weightGoalField = form
      ? getFormField(form, "weightGoal")
      : null;

    if (
      !form ||
      !results ||
      !errorElement ||
      !feedback ||
      !feedbackTitle ||
      !feedbackDescription ||
      !quickWeightSelect ||
      !nextButton ||
      goalButtons.length !== 4 ||
      toolButtons.length !== 3
    ) {
      return;
    }

    root.dataset.nnavInitialized = "1";

    var state = {
      goal: "muscle",
      tool: "calories",
      calculation: null,
      dirty: true
    };

    function invalidateResults() {
      state.calculation = null;
      state.dirty = true;

      results.hidden = true;
      hideError(errorElement);
    }

    function updateConditionalFields() {
      if (weightArea) {
        weightArea.hidden =
          state.goal !== "weight";
      }

      if (proteinArea) {
        proteinArea.hidden =
          state.tool !== "protein" &&
          state.tool !== "macros";
      }
    }

    function updateGoalButtons() {
      goalButtons.forEach(function (button) {
        var active =
          button.getAttribute(
            "data-goal"
          ) === state.goal;

        button.classList.toggle(
          "is-active",
          active
        );

        button.setAttribute(
          "aria-pressed",
          String(active)
        );
      });
    }

    function updateToolButtons() {
      toolButtons.forEach(function (button) {
        var active =
          button.getAttribute(
            "data-nnav-tool"
          ) === state.tool;

        button.classList.toggle(
          "is-active",
          active
        );

        button.setAttribute(
          "aria-pressed",
          String(active)
        );
      });
    }

    function updateGoalFeedback() {
      var content =
        GOAL_CONTENT[state.goal];

      if (!content) return;

      feedback.setAttribute(
        "data-active-goal",
        state.goal
      );

      feedbackTitle.textContent =
        content.title;

      feedbackDescription.textContent =
        content.description;

      if (feedbackWeightArea) {
        feedbackWeightArea.hidden =
          state.goal !== "weight";
      }

      if (weightGoalField) {
        quickWeightSelect.value =
          weightGoalField.value;
      }
    }

    function updateSubmitButton() {
      var submit = getElement(
        root,
        "[data-nnav-submit]"
      );

      if (!submit) return;

      var label = submit.querySelector(
        "span:first-child"
      );

      if (!label) return;

      if (state.tool === "calories") {
        label.textContent =
          "KALORIEN BERECHNEN";
      } else if (state.tool === "protein") {
        label.textContent =
          "PROTEIN BERECHNEN";
      } else {
        label.textContent =
          "MAKROS BERECHNEN";
      }
    }

    function updateToolUI() {
      updateToolButtons();
      updateConditionalFields();
      updateSubmitButton();

      showRelevantPanel(
        root,
        state.tool
      );

      if (
        state.calculation &&
        !state.dirty
      ) {
        results.hidden = false;
      } else {
        results.hidden = true;
      }

      hideError(errorElement);
    }

    function selectGoal(goal) {
      if (
        VALID_GOALS.indexOf(goal) === -1
      ) {
        return;
      }

      if (state.goal !== goal) {
        state.goal = goal;
        invalidateResults();
      }

      updateGoalButtons();
      updateGoalFeedback();
      updateConditionalFields();
    }

    function selectTool(tool) {
      if (
        VALID_TOOLS.indexOf(tool) === -1
      ) {
        return;
      }

      if (state.tool === tool) {
        return;
      }

      state.tool = tool;
      updateToolUI();
    }

    /*
      ZIELKARTEN
    */

    goalButtons.forEach(function (button) {
      button.addEventListener(
        "click",
        function () {
          selectGoal(
            button.getAttribute("data-goal")
          );
        }
      );
    });

    /*
      SCHNELLE GEWICHTSZIELAUSWAHL
    */

    quickWeightSelect.addEventListener(
      "change",
      function () {
        if (weightGoalField) {
          weightGoalField.value =
            quickWeightSelect.value;
        }

        invalidateResults();
      }
    );

    if (weightGoalField) {
      weightGoalField.addEventListener(
        "change",
        function () {
          quickWeightSelect.value =
            weightGoalField.value;

          invalidateResults();
        }
      );
    }

    /*
      WEITER-BUTTON
    */

    nextButton.addEventListener(
      "click",
      function () {
        var tools = getElement(
          root,
          "[data-nnav-tools]"
        );

        scrollToElement(tools);
      }
    );

    /*
      WERKZEUGAUSWAHL
    */

    toolButtons.forEach(function (button) {
      button.addEventListener(
        "click",
        function () {
          selectTool(
            button.getAttribute(
              "data-nnav-tool"
            )
          );
        }
      );
    });

    /*
      EINGABEN ÄNDERN
    */

    form.addEventListener(
      "input",
      invalidateResults
    );

    form.addEventListener(
      "change",
      invalidateResults
    );

    /*
      BERECHNUNG STARTEN
    */

    form.addEventListener(
      "submit",
      function (event) {
        event.preventDefault();

        hideError(errorElement);

        if (
          !window.NemeanNavigatorCalc ||
          typeof window.NemeanNavigatorCalc.calculate !==
            "function"
        ) {
          results.hidden = true;

          showError(
            errorElement,
            "Der Rechner ist noch nicht " +
            "vollständig geladen. " +
            "Bitte lade die Seite neu."
          );

          return;
        }

        var inputs = readInputs(
          form,
          state.goal
        );

        try {
          var calculation =
            window.NemeanNavigatorCalc.calculate(
              inputs
            );

          fillAllResults(
            root,
            calculation
          );

          state.calculation = calculation;
          state.dirty = false;

          showRelevantPanel(
            root,
            state.tool
          );

          results.hidden = false;
          scrollToElement(results);

        } catch (error) {
          state.calculation = null;
          state.dirty = true;
          results.hidden = true;

          var knownValidation =
            error &&
            error.name ===
              "NemeanValidationError";

          var message = knownValidation
            ? error.message
            : "Die Berechnung konnte nicht " +
              "abgeschlossen werden. " +
              "Bitte überprüfe deine Angaben.";

          showError(
            errorElement,
            message
          );

          if (
            knownValidation &&
            error.field &&
            error.field !== "general"
          ) {
            var problemField =
              getFormField(
                form,
                error.field
              );

            if (
              problemField &&
              typeof problemField.focus ===
                "function"
            ) {
              problemField.focus();
            }
          }
        }
      }
    );

    /*
      STARTZUSTAND
    */

    updateGoalButtons();
    updateGoalFeedback();
    updateToolUI();

    results.hidden = true;
    hideError(errorElement);
  }

  function initAllNavigators(container) {
    var context = container || document;

    if (
      context.matches &&
      context.matches(ROOT_SELECTOR)
    ) {
      initNavigator(context);
    }

    var roots = context.querySelectorAll
      ? context.querySelectorAll(
          ROOT_SELECTOR
        )
      : [];

    roots.forEach(function (root) {
      initNavigator(root);
    });
  }

  /*
    NORMALES LADEN
  */

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      function () {
        initAllNavigators(document);
      }
    );
  } else {
    initAllNavigators(document);
  }

  /*
    SHOPIFY THEME-EDITOR
  */

  document.addEventListener(
    "shopify:section:load",
    function (event) {
      initAllNavigators(
        event.target || document
      );
    }
  );
})();
